/**
 * FORMATTING — one implementation, every consumer.
 *
 * OWNER: app-architect. Promoted out of `apps/web/src/lib` by ruling R-9 after
 * the D-025 harness surfaced it as a fork (F3). Web, the native harness, the
 * back office and the print view all read THIS module. A second formatter is
 * how "£4,340" and "£4,340.00" end up on two surfaces both claiming to be the
 * same price, which `EL-PRICE` and [T-48] forbid outright.
 *
 * The fork was not hypothetical: while it stood, the two copies had DIFFERENT
 * `dateInWords` behaviour for a date-only value, and a client west of Greenwich
 * was shown the day before his tailor measured him — on the measurements
 * record, under a provenance line whose whole job is to say when. Both copies
 * have since been corrected; promoting the module is what stops the next such
 * divergence from needing to be found twice. The surviving implementation is
 * the one web arrived at (see `dateInWords`).
 *
 * PLATFORM-FREE. `Intl` only: no DOM, no `node:*`, no React, no `react-native`.
 *
 * Every rule here is a presentation rule from a binding source, and none of them
 * invents a word:
 *   · Money — minor units in, the CURRENCY CODE FROM THE SERVER out. No symbol
 *     is written anywhere in this repository's web source ([T-48], D-027).
 *   · Dates — `d MMMM yyyy`, in words, never `14/03/24`, never relative
 *     (design/copy.json → slots.takenAtDate).
 *   · Metreage — one decimal place (OPS_SPEC §4.1).
 *   · Durations — produced by `Intl`, so "18 months" is locale data rather than
 *     an authored English string.
 */

/**
 * Two locales, deliberately. The atelier's voice is British English — the copy
 * catalogue is `en-GB` and dates are the binding `d MMMM yyyy` (design/copy.json
 * → slots.takenAtDate), so dates, counts and durations format `en-GB`. MONEY is
 * the one exception: D-071 (amending D-027) makes the trading currency USD, and
 * `en-GB` renders that as `US$4,340` — a symbol no US client's own bank uses —
 * so money formats `en-US`: `$4,340` at home, and a historic GBP order still
 * renders `£4,340` because the code always comes from the server.
 */
const LOCALE = 'en-GB';
const MONEY_LOCALE = 'en-US';

/**
 * `$4,340`. Whole dollars unless a figure on the same screen carries cents, in
 * which case every figure on that screen shows two decimals — consistency over
 * compactness (COMPONENT_SPEC §5.1).
 */
export function money(minor: number, currency: string, withPence = false): string {
  return new Intl.NumberFormat(MONEY_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: withPence ? 2 : 0,
    maximumFractionDigits: withPence ? 2 : 0,
  }).format(minor / 100);
}

/**
 * THE INVERSE OF `money()`, and the only place a typed amount becomes minor
 * units. ROAD-2, 1 September 2026.
 *
 * WHY IT IS HERE AND NOT IN THE SCREEN. This is the one arithmetic in the
 * product where a rounding error is a person's money. `Math.round(Number(text)
 * * 100)` — the obvious one line — is wrong for a value a tailor will really
 * type: `Number('8.07') * 100` is 806.9999999999999, and `Math.round` saves it
 * only until the day it does not (`1.005` → 100, not 101). So the fraction is
 * read as DIGITS, never as a float: the string is split at the point and the
 * two halves are integers before they are ever added.
 *
 * WHAT IT REFUSES, rather than guesses:
 *   · `precision` — more than two decimal places. A third digit is either a typo
 *     or a currency this house does not trade in, and silently dropping a man's
 *     money is not a rounding convention.
 *   · `shape` — anything that is not digits with at most one point. A screen
 *     that reads "1,2.50" as anything at all is a screen inventing an intent.
 *   · `negative` — a refund is an EVENT against an intent, never a negative
 *     amount typed into one.
 *   · `empty` — said separately from `shape`, because an empty box is not a
 *     mistake, it is an unfinished sentence, and the two deserve different words.
 *
 * It accepts thousand separators and spaces, because a man typing 1,250 means
 * 1250 and refusing that would be pedantry rather than care. It accepts a
 * leading currency symbol for the same reason.
 *
 * NO CAP. There is no maximum here, invented or otherwise (law 32(d)) — what a
 * house will take is Paul's sentence, not this function's. The screen names the
 * arithmetic against what is outstanding and lets a person decide.
 */
export type MinorRefusal = 'empty' | 'shape' | 'precision' | 'negative';

export function minorFromTyped(text: string): { minor: number } | { refused: MinorRefusal } {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return { refused: 'empty' };
  if (trimmed.startsWith('-') || trimmed.startsWith('\u2212')) return { refused: 'negative' };
  /* A leading symbol and the separators a person actually types. Nothing else
     is stripped: a stray letter must reach the shape refusal, not vanish. */
  const bare = trimmed.replace(/^[$£€]\s*/, '').replace(/[,\s]/g, '');
  if (!/^\d*\.?\d*$/.test(bare) || bare === '' || bare === '.') return { refused: 'shape' };
  const point = bare.indexOf('.');
  const whole = point === -1 ? bare : bare.slice(0, point);
  const fraction = point === -1 ? '' : bare.slice(point + 1);
  if (fraction.length > 2) return { refused: 'precision' };
  /* Both halves are integers before anything is added. `padEnd` is what makes
     "8.7" seven-tenths of a dollar rather than seven cents. */
  const minor = Number(whole === '' ? '0' : whole) * 100 + Number(fraction.padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(minor)) return { refused: 'shape' };
  if (minor <= 0) return { refused: 'empty' };
  return { minor };
}

/** True when any figure in the set carries pence, so the screen shows two decimals throughout. */
export function anyPence(minors: readonly number[]): boolean {
  return minors.some((m) => m % 100 !== 0);
}

/** A signed money delta for the [T-54] diff. The minus sign and nothing else. */
export function moneyDelta(minor: number, currency: string, withPence = false): string {
  const formatted = money(Math.abs(minor), currency, withPence);
  return minor < 0 ? `−${formatted}` : formatted;
}

/**
 * `14 March 2024`.
 *
 * A DATE-ONLY value is a CALENDAR DATE, not an instant. `takenAt` is the day a
 * tailor measured a body; it has no time and no timezone, and rendering it
 * through a local-time `Date` shifts it a day west of Greenwich — the seeded
 * `2025-11-02` came out as "1 November 2025" for anyone at a negative offset.
 * That is a measurement record showing the wrong date, which is not a rounding
 * error in this product. Date-only values are therefore formatted in UTC, which
 * is the only reading of "2025-11-02" that is the same everywhere.
 *
 * A full instant (`2026-08-09T05:19:13.460Z`) still renders in local time,
 * because an event DID happen at a moment and the reader's moment is the right
 * frame for it.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function dateInWords(iso: string): string {
  const dateOnly = DATE_ONLY.test(iso);
  const date = new Date(dateOnly ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(dateOnly ? { timeZone: 'UTC' } : {}),
  }).format(date);
}

/** `14 March 2024, 09:41` — staff and print surfaces only. */
export function dateTimeInWords(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** `09:41` — the time part alone, for a conflict message. */
export function timeOfDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit' }).format(date);
}

/** `2024-03-14`, for a date input's value. Carries no body data ([T-33]). */
export function dateInputValue(iso: string): string {
  if (DATE_ONLY.test(iso)) return iso;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** `4.6` — metreage, one decimal, always. */
export function metres(value: number): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

/** A signed metreage for the ledger. The minus sign and nothing else. */
export function metresSigned(value: number): string {
  return `${value < 0 ? '−' : '+'}${metres(Math.abs(value))}`;
}

export function decimal(value: number, places = 1): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(value);
}

export function integer(value: number): string {
  return new Intl.NumberFormat(LOCALE).format(value);
}

/**
 * `18 months` / `2 years`, produced entirely by `Intl` unit formatting. The
 * catalogue carries no vocabulary for this slot, so taking the words from ICU
 * locale data is the only route that authors nothing (see the round report).
 */
export function ageInWords(fromIso: string, nowIso?: string): string {
  const from = new Date(fromIso).getTime();
  const now = nowIso === undefined ? Date.now() : new Date(nowIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(now)) return '';
  const months = Math.max(0, Math.floor((now - from) / (1000 * 60 * 60 * 24 * 30.4375)));
  const unit = months >= 24 ? 'year' : 'month';
  const count = months >= 24 ? Math.floor(months / 12) : months;
  return new Intl.NumberFormat(LOCALE, { style: 'unit', unit, unitDisplay: 'long' }).format(count);
}

export function monthsSince(fromIso: string, nowIso?: string): number {
  const from = new Date(fromIso).getTime();
  const now = nowIso === undefined ? Date.now() : new Date(nowIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(now)) return 0;
  return Math.max(0, Math.floor((now - from) / (1000 * 60 * 60 * 24 * 30.4375)));
}

export function daysSince(fromIso: string, nowIso?: string): number {
  const from = new Date(fromIso).getTime();
  const now = nowIso === undefined ? Date.now() : new Date(nowIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(now)) return 0;
  return Math.max(0, Math.floor((now - from) / (1000 * 60 * 60 * 24)));
}

/** `100% wool` / `88% wool, 12% mohair` — the cloth's own composition, as data. */
export function compositionLine(
  parts: readonly { readonly fibre: string; readonly pct: number }[],
): string {
  return parts.map((part) => `${integer(part.pct)}% ${part.fibre}`).join(', ');
}

/** `A, B and C` — no serial comma (design/copy.json → conventions.lists). */
export function joinList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0] as string;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] as string}`;
}

/** First 12 of a content hash — the form every staff surface and the cut sheet use. */
export function shortHash(hash: string): string {
  return hash.slice(0, 12);
}

/** `1 September – 26 October` (an en dash, from the copy's own convention). */
export function windowInWords(earliest: string, latest: string): [string, string] {
  return [dateInWords(earliest), dateInWords(latest)];
}
